import React, { useState, useRef, useCallback, useEffect } from 'react';

import { useTheme } from 'styled-components';

import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  Crop,
  PixelCrop,
  convertToPixelCrop,
} from 'react-image-crop';

import { AiFillCloseCircle } from 'react-icons/ai';
import { BsFillAspectRatioFill } from 'react-icons/bs';
import { MdZoomOutMap, MdRotate90DegreesCcw } from 'react-icons/md';

import { canvasPreview } from './canvasPreview';

import Button from '../Form/Button';
import Cancel from '../Form/Cancel';

import {
  Container,
  Content,
  Left,
  Right,
  Preview,
  Header,
  Center,
  Footer,
  ImageCrop,
  Controls,
  Control,
} from './styles';

import { CropperProps } from './interface';

import 'react-image-crop/dist/ReactCrop.css';

function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number,
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  );
}

const Plate: React.FC<CropperProps> = ({
  title,
  base64,
  onApplyCrop,
  onClose,
}) => {

  const { colors } = useTheme();

  const ref = useRef<HTMLDivElement>(null);

  const [imgSrc, setImgSrc] = useState(base64);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [aspect, setAspect] = useState<number | undefined>(16 / 9);

  const onSave = () => {
    onApplyCrop(imgSrc);
    setImgSrc(imgSrc);
  };

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (aspect) {
      const { width, height } = e.currentTarget;
      setCrop(centerAspectCrop(width, height, aspect));
    }
  };

  const handlerShortCuts = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      onClose();
    }
  };

  const toggleAspect = () => {
    if (aspect) {
      setAspect(undefined);
    } else {
      setAspect(16 / 9);

      if (imgRef.current) {
        const { width, height } = imgRef.current;
        const newCrop = centerAspectCrop(width, height, 16 / 9);
        setCrop(newCrop);
        setCompletedCrop(convertToPixelCrop(newCrop, width, height));
      }
    }
  };

  const toggleRotate = () => {
    let _rotate = rotate;
    _rotate += 90;
    if (_rotate >= 360) {
      _rotate = 0;
    }
    setRotate(_rotate);
  };

  const toggleZoom = () => {
    let zoom = scale + 1;
    if (zoom > 3) {
      zoom = 1;
    }
    setScale(zoom);
  };

  const handleClickOutside = useCallback(
    (event: Event): void => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (
      completedCrop?.width &&
      completedCrop?.height &&
      imgRef.current &&
      previewCanvasRef.current
    ) {
      canvasPreview(
        imgRef.current,
        previewCanvasRef.current,
        completedCrop,
        scale,
        rotate,
      );
    }
  }, [completedCrop, scale, rotate]);

  useEffect(() => {
    document.addEventListener('keydown', handlerShortCuts, true);
    document.addEventListener('click', handleClickOutside, true);

    return () => {
      document.removeEventListener('keydown', handlerShortCuts, true);
      document.removeEventListener('click', handleClickOutside, true);
    };
  });

  return (
    <Container>
      <Content ref={ref}>
        <Left>
          <Header>
            <h1>{title}</h1>
            <button type="button" onClick={onClose}>
              <AiFillCloseCircle size={46} color={colors.IC1} />
            </button>
          </Header>

          <Controls>
            <Control onClick={toggleAspect} title={'Aspecto'}>
              <BsFillAspectRatioFill
                size={32}
                color={aspect ? '#333' : '#ccc'}
              />
            </Control>
            <Control onClick={toggleZoom} title="Zoom">
              <MdZoomOutMap size={32} color={'#333'} />
            </Control>

            <Control onClick={toggleRotate} title="Girar Imagem">
              <MdRotate90DegreesCcw size={32} color={'#333'} />
            </Control>
          </Controls>

          <Center>
            {!!imgSrc && (
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={c => setCompletedCrop(c)}
                aspect={aspect}
                minHeight={200}
              >
                <ImageCrop
                  ref={imgRef}
                  alt="Recorte-me"
                  src={imgSrc}
                  style={{ transform: `scale(${scale}) rotate(${rotate}deg)` }}
                  onLoad={onImageLoad}
                />
              </ReactCrop>
            )}
          </Center>

          <Footer>
            <Cancel onClick={onClose}>Cancelar</Cancel>
            <Button onClick={onSave}>SALVAR</Button>
          </Footer>
        </Left>
        <Right>
          <Preview>
            {!!completedCrop && (
              <>
                <div>
                  <canvas
                    ref={previewCanvasRef}
                    style={{
                      border: '1px solid black',
                      objectFit: 'contain',
                      width: completedCrop.width,
                      height: completedCrop.height,
                    }}
                  />
                </div>
              </>
            )}
          </Preview>
        </Right>
      </Content>
    </Container>
  );
};

export default Plate;
